import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { TeamSearchCombobox } from '@/components/venue/TeamSearchCombobox';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useUnifiedUser } from '@/hooks/useUnifiedUser';

interface GameCodeRegistrationProps {
  onSuccess?: () => void;
}

interface FormData {
  game_code: string;
  team_id: number;
  team_side: 'home' | 'away';
}

export const GameCodeRegistration: React.FC<GameCodeRegistrationProps> = ({ onSuccess }) => {
  const { user } = useUnifiedUser();
  const [loading, setLoading] = useState(false);
  const [selectedTeamName, setSelectedTeamName] = useState('');
  
  const { register, handleSubmit, watch, setValue, formState: { errors } } = useForm<FormData>();

  const onSubmit = async (data: FormData) => {
    if (!user) {
      toast.error('You must be logged in to register for games');
      return;
    }

    if (!data.team_id) {
      toast.error('Please select a team');
      return;
    }

    setLoading(true);

    try {
      // First, verify the game code exists and get game details
      const { data: gameData, error: gameError } = await supabase
        .from('venue_games')
        .select('*')
        .eq('game_code', data.game_code.toUpperCase())
        .single();

      if (gameError || !gameData) {
        toast.error('Invalid game code. Please check and try again.');
        setLoading(false);
        return;
      }

      // Check if the team side is already taken
      const sideField = data.team_side === 'home' ? 'home_team_id' : 'away_team_id';
      if (gameData[sideField]) {
        toast.error(`The ${data.team_side} team position is already taken for this game.`);
        setLoading(false);
        return;
      }

      // Update the game with the team assignment
      const updateData = {
        [sideField]: data.team_id,
        [`${data.team_side}_team_status`]: 'pending'
      };

      const { error: updateError } = await supabase
        .from('venue_games')
        .update(updateData)
        .eq('id', gameData.id);

      if (updateError) {
        console.error('Error updating game:', updateError);
        toast.error('Failed to register team for game. Please try again.');
        setLoading(false);
        return;
      }

      // Send notification to venue and coaches
      const gameTitle = `${data.team_side === 'home' ? selectedTeamName : 'TBD'} vs ${data.team_side === 'away' ? selectedTeamName : 'TBD'}`;
      const gameDate = new Date(gameData.game_date).toLocaleDateString();
      
      await supabase
        .from('game_notifications')
        .insert({
          game_id: gameData.id,
          recipient_id: gameData.venue_id,
          recipient_type: gameData.venue_id.includes('-') ? 'internal' : 'supabase',
          notification_type: 'team_registration',
          message: `Team ${selectedTeamName} has registered as the ${data.team_side} team for the game on ${gameDate} using code ${data.game_code}.`,
          requires_action: false
        });

      toast.success(`Successfully registered ${selectedTeamName} as the ${data.team_side} team!`);
      
      // Navigate to the unified game form for the registered game
      setTimeout(() => {
        window.location.href = `/games/${gameData.id}/unified-form`;
      }, 1000);
      
      if (onSuccess) {
        onSuccess();
      }

    } catch (error) {
      console.error('Error registering team:', error);
      toast.error('An error occurred while registering. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="w-full max-w-md">
      <CardHeader>
        <CardTitle className="text-center">Register Team for Game</CardTitle>
        <CardDescription className="text-center">
          Enter your game code to register your team
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="game_code">Game Code *</Label>
            <Input
              id="game_code"
              {...register('game_code', { 
                required: 'Game code is required',
                minLength: { value: 6, message: 'Game code must be 6 characters' },
                maxLength: { value: 6, message: 'Game code must be 6 characters' }
              })}
              placeholder="Enter 6-character game code"
              className="uppercase"
              maxLength={6}
            />
            {errors.game_code && (
              <p className="text-sm text-red-500">{errors.game_code.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label>Select Your Team *</Label>
            <TeamSearchCombobox
              value={watch('team_id')}
              onSelect={(teamId, teamName) => {
                setValue('team_id', Number(teamId));
                setSelectedTeamName(teamName);
              }}
              placeholder="Search for your team"
            />
            {selectedTeamName && (
              <p className="text-sm text-green-400">✓ Selected: {selectedTeamName}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="team_side">Team Position *</Label>
            <Select 
              value={watch('team_side')} 
              onValueChange={(value: 'home' | 'away') => setValue('team_side', value)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select home or away" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="home">Home Team</SelectItem>
                <SelectItem value="away">Away Team</SelectItem>
              </SelectContent>
            </Select>
            {errors.team_side && (
              <p className="text-sm text-red-500">Please select team position</p>
            )}
          </div>

          <Button 
            type="submit" 
            className="w-full" 
            disabled={loading || !selectedTeamName || !watch('team_side')}
          >
            {loading ? 'Registering...' : 'Register Team'}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};